export type PermissionLevel = 'read' | 'update' | 'delete' | 'view' | 'create_instance';
export type ResourceType = 'application_resource' | 'entity_type' | 'entity_resource';

export interface AssignedGroupDetail {
  group: string;
  permissions: PermissionLevel[];
  userCount: number;
}

interface ResourceRowData {
  resource: string;
  resourceId: string;
  resourceType: ResourceType;
  assignedGroups: string[];
  userCount: number;
  entityType?: string;
}

export function groupHasResource(
  group: any,
  resourceId: string,
  resourceName: string,
  resourceType: ResourceType
): boolean {
  if (resourceType === 'application_resource') {
    return group.application_resources?.some((r: any) => r.application_resource === resourceId) ?? false;
  }
  if (resourceType === 'entity_type') {
    return group.entity_types?.some((r: any) => r.entity_type === resourceId) ?? false;
  }
  return group.entity_resources?.some((r: any) => r.resource_name === resourceName) ?? false;
}


export function getGroupNamesForResource(
  groups: any[],
  resourceId: string,
  resourceName: string,
  resourceType: ResourceType
): string[] {
  const names: string[] = [];
  for (const group of groups) {
    if (groupHasResource(group, resourceId, resourceName, resourceType) && !names.includes(group.group)) {
      names.push(group.group);
    }
  }
  return names;
}

/**
 * Aggregates flat resource-groups API entries (one row per group+relation) into
 * deduplicated AssignedGroupDetail[] (one row per group, permissions collected).
 * memberCountMap is keyed by group name and provides the userCount.
 */
export function aggregateResourceGroupEntries(
  entries: Array<{ group: string; relation: string }>,
  memberCountMap: Map<string, number>
): AssignedGroupDetail[] {
  const groupMap = new Map<string, AssignedGroupDetail>();
  for (const entry of entries) {
    if (!groupMap.has(entry.group)) {
      groupMap.set(entry.group, {
        group: entry.group,
        permissions: [],
        userCount: memberCountMap.get(entry.group) || 0,
      });
    }
    const detail = groupMap.get(entry.group)!;
    const relation = entry.relation as PermissionLevel;
    if (!detail.permissions.includes(relation)) {
      detail.permissions.push(relation);
    }
  }
  return Array.from(groupMap.values());
}

export function buildAllResourcesWithData(
  resourcesResponse: any,
  groups: any[]
): ResourceRowData[] {
  if (!resourcesResponse) return [];

  const allResourcesList: ResourceRowData[] = [];

  (resourcesResponse.application_resources || []).forEach((appResource: any) => {
    const resourceName = appResource.resource_name || appResource.application_resource;
    const resourceId = appResource.application_resource;
    allResourcesList.push({
      resource: resourceName,
      resourceId,
      resourceType: 'application_resource',
      assignedGroups: getGroupNamesForResource(groups, resourceId, resourceName, 'application_resource'),
      userCount: appResource.user_count || 0,
    });
  });

  (resourcesResponse.entity_types || []).forEach((entityType: any) => {
    const resourceName = entityType.entity_type;
    const resourceId = entityType.entity_type;
    allResourcesList.push({
      resource: resourceName,
      resourceId,
      resourceType: 'entity_type',
      assignedGroups: getGroupNamesForResource(groups, resourceId, resourceName, 'entity_type'),
      userCount: entityType.user_count || 0,
    });
  });

  (resourcesResponse.entity_resources || []).forEach((entityResource: any) => {
    const resourceName = entityResource.resource_name;
    const resourceId = entityResource.entity_resource || entityResource.id;
    allResourcesList.push({
      resource: resourceName,
      resourceId,
      resourceType: 'entity_resource',
      assignedGroups: getGroupNamesForResource(groups, resourceId, resourceName, 'entity_resource'),
      userCount: entityResource.user_count || 0,
      entityType: entityResource.entity_type || '',
    });
  });

  return allResourcesList;
}

export function buildUserWithGroups(user: any) {
  return { ...user, groups: user.assigned_groups || [] };
}
